import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  localize,
  Logger,
  useIsTouchDevice,
  usePendo,
  useResponsiveValueRedux,
} from '@saviynt/common';
import {
  AlertBannerProvider,
  Box,
  ModalIsland,
  StackingAlertBanner,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';
import Lottie from 'lottie-react';
import momentTimezone from 'moment-timezone';

import LandscapeModeIcon from '../../ECMv6/assets/images/lotties/landscapemode-loader.json';
import { CertsListSidePanel } from '../../components/CertsList';
import CertsListActionAlerts from '../../components/CertsList/CertsListAction/CertsListActionAlerts';
import CertsExportAlert from '../../components/CertsList/CertsListAction/CertsListExport/CertsExportAlerts';
import {
  CERTS_LIST_DONE_TAB,
  CERTS_LIST_TODO_TAB,
} from '../../components/CertsList/constant';
import { CertsGlobalError } from '../../components/Common';
import {
  getCertListTabsCount,
  getViewDetails,
  setActiveTab,
  setGlobalDateFormat,
  setGlobalError,
  setGlobalLoading,
  setIsWithoutCampaignKey,
  setListCampaignKey,
  setListTimeZone,
  updateScreenSize,
  updateTouchDevice,
} from '../../redux/certlist/action';
import { REDIRECT_FLAG, SCREEN_WIDTH_QUERIES } from '../../utils/constant';
import { getMessageKey } from '../../utils/utils';

import CertsListBody from './CertsListBody';
// import CertsListFooter from './CertsListFooter'; // Will be done when bulk is available
import CertsListHeader from './CertsListHeader';

import './CertificationList.css';

function CertificationList() {
  // Pendo
  usePendo();

  // Globals
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const campaignKeyParam = queryParams.get('campaignKey');

  useResponsiveValueRedux(updateScreenSize, SCREEN_WIDTH_QUERIES);

  // Redux
  const dispatch = useDispatch();
  const {
    global: { campaignKey, isWithoutCampaignKey, isGlobalError },
    sidePanel: { isSidePanelVisible },
    lineAction: { certsListExport } = {},
    tabs: { activeTab },
  } = useSelector((state) => state?.certifications?.list);
  const {
    isMenuPinned,
    headerMenu: { ecmDateFormat },
  } = useSelector((state) => state?.user);
  const intl = useIntl();
  const { messages } = useSelector((state) => state?.user) || {};

  // Landscape mode and height below 660px
  const isTouchDevice = useIsTouchDevice();
  const [isPotrait, setIsPotrait] = useState(true);
  const [isPotraitModalOpen, setIsPotraitModalOpen] = useState(false);

  // Local States and flags
  const [successAlertBanner, setSuccessAlertBanner] = useState({
    isOpen: false,
    message: '',
  });

  // Setting global date format for list page
  useEffect(() => {
    if (ecmDateFormat && ecmDateFormat?.length > 0) {
      dispatch(setGlobalDateFormat(ecmDateFormat));
    }
  }, []);

  // Global flag for touch devices
  useEffect(() => {
    dispatch(updateTouchDevice(isTouchDevice));

    const windowMatchMediaLandscape = window.matchMedia(
      '(orientation: landscape) and (max-height: 659px)'
    );

    // Check for the landscape mode
    const checkLandscapeMode = (e) => {
      if (isTouchDevice) {
        setIsPotrait(!e.matches);
        setIsPotraitModalOpen(e.matches);
      }
    };

    windowMatchMediaLandscape.addEventListener('change', checkLandscapeMode);

    return () => {
      windowMatchMediaLandscape.removeEventListener(
        'change',
        checkLandscapeMode
      );
    };
  }, [isTouchDevice]);

  /**
   * Dispatches an action to set a global error state with the provided error message.
   *
   * @param {string|null} errorMsg - The error message to be displayed in the global error modal. If null, no specific error message is provided.
   * @returns {void} This function does not return a value.
   */
  const raiseGlobalErrorModal = (errorMsg = null) =>
    dispatch(setGlobalError({ isError: true, error: errorMsg }));

  /**
   * useEffect hook to validate and set the campaign key from URL parameters.
   * If the campaign key is a valid numeral converted string, it dispatches an action to set the campaign key.
   * Otherwise, it raises a global error modal indicating that no valid campaign key is provided.
   *
   * @param {string} campaignKeyParam - The campaign key extracted from the URL query parameters.
   * @returns {void} This effect does not return a value.
   */
  useEffect(() => {
    // To check that campaign key is valid numeral conveted string
    if (/^\d+$/.test(campaignKeyParam)) {
      dispatch(setListCampaignKey(campaignKeyParam));

      return;
    }

    dispatch(setIsWithoutCampaignKey(true));
  }, [campaignKeyParam]);

  /**
   * Handles the initialization of the landing tab based on session storage data.
   *
   * This function checks for a specific session storage item (`certificationDetailToList`)
   * to determine the appropriate landing tab and optionally display a success banner.
   * If the item is not found or expired, it falls back to the default "To-Do" tab.
   *
   * Behavior:
   * - If session storage contains valid data with a positive timestamp difference:
   *   - Sets the active tab to "Done."
   *   - Displays a success alert banner with a message from the session storage data.
   *   - Removes the session storage item after processing.
   * - If session storage is absent or expired:
   *   - Sets the active tab to "To-Do."
   * - If no active tab is already set, the function ensures a tab is selected.
   */
  const handleLandingTab = () => {
    const encodedData = sessionStorage.getItem(REDIRECT_FLAG);

    if (!encodedData) {
      if (!activeTab) dispatch(setActiveTab(CERTS_LIST_TODO_TAB));

      return;
    }

    const data = JSON.parse(encodedData);
    const diff = Date.now() - data.code;

    if (diff > 0) {
      if (!activeTab) dispatch(setActiveTab(CERTS_LIST_DONE_TAB));
      setSuccessAlertBanner({
        isOpen: true,
        message: data.messsage,
      });
      sessionStorage.removeItem(REDIRECT_FLAG);

      return;
    }

    if (!activeTab) dispatch(setActiveTab(CERTS_LIST_TODO_TAB));
  };

  /**
   * This effect hook is responsible for setting the time zone and fetching the certification list view details.
   * It runs when the 'campaignKey' prop changes.
   * @param {string} campaignKey - The campaign key extracted from the URL query parameters.
   * @param {string} timeZone - The guessed time zone of the user's device.
   * @param {function} setListTimeZone - The Redux action to set the time zone.
   * @param {function} getViewDetails - The Redux action to fetch the certification list view details.
   * @param {function} raiseGlobalErrorModal - The function to raise a global error modal.
   * @returns {void} This effect does not return a value.
   */
  useEffect(() => {
    const timeZone = momentTimezone.tz.guess();
    const timeZoneOffset = new Date().getTimezoneOffset();
    const timeZoneAbbrevation = momentTimezone.tz
      .zone(timeZone)
      .abbr(timeZoneOffset);

    dispatch(setListTimeZone(timeZoneAbbrevation));

    if (campaignKey || isWithoutCampaignKey) {
      dispatch(getViewDetails())
        .then(() => {
          Logger.log('Config loaded successfully');
          dispatch(getCertListTabsCount());

          // Checking if redirected from certification details page after locking
          handleLandingTab();
        })
        .catch((error) => {
          raiseGlobalErrorModal(error?.message);
        })
        .finally(() => {
          dispatch(setGlobalLoading(false));
        });
    }
  }, [campaignKey, isWithoutCampaignKey]);

  const alertWrapperClasses = cx(
    'CertificationList-AlertBannerProvider',
    isSidePanelVisible &&
      'CertificationList-AlertBannerProvider--isSidePanelVisible',
    isMenuPinned && 'CertificationList-AlertBannerProvider--isMenuPinned'
  );
  const pageClasses = cx(
    'CertificationList',
    isSidePanelVisible && 'CertificationList--isSidePanelVisible'
  );

  const wrapperClasses = cx(
    'CertificationList-wrapperContainer',
    isSidePanelVisible &&
      'CertificationList-wrapperContainer--isSidePanelVisible',
    isMenuPinned && 'CertificationList-wrapperContainer--isMenuPinned'
  );

  const certsListExportStatus = certsListExport?.certsListExportStatus;

  return (
    <>
      <AlertBannerProvider className={alertWrapperClasses}>
        <section className={pageClasses} data-testid='cert-list'>
          <Box className={wrapperClasses}>
            <CertsListHeader />
            <CertsListBody />
            {/* Will be done when bulk is available */}
            {/* <CertsListFooter /> */}
            <CertsExportAlert exportStatus={certsListExportStatus} />
            <CertsListActionAlerts />
          </Box>
          <CertsListSidePanel />
          <Box className={!isPotrait ? 'CertificationList-whiteOverlay' : ''}>
            {isPotraitModalOpen && (
              <ModalIsland
                onClose={() => setIsPotraitModalOpen(false)}
                isOpen={isPotraitModalOpen}
                className='CertificationList-landscapeModal'
                overlayBackgroundColor='#8691a4'
                contentBody={
                  <>
                    <Lottie
                      className='CertificationList-landscapeModalIcon'
                      animationData={LandscapeModeIcon}
                    />
                    <Typography
                      kind='body1'
                      desktop='body1-d'
                      mobile='body1-d'
                      className='CertificationList-landscapeModalText'>
                      For better experience, please switch to portrait mode.
                    </Typography>
                  </>
                }
              />
            )}
          </Box>
        </section>
        <StackingAlertBanner
          colorTheme='Success'
          title={
            messages.CERTS_ACTION_ALERTS_SUCCESS ||
            localize(intl, getMessageKey('CERTS_ACTION_ALERTS_SUCCESS'))
          }
          description={successAlertBanner?.message}
          isVisible={successAlertBanner?.isOpen}
          dataTestId='alert-success'
          onCancel={() => {
            setSuccessAlertBanner((prev) => ({
              ...prev,
              isOpen: false,
            }));
          }}
        />
      </AlertBannerProvider>

      {isGlobalError && <CertsGlobalError />}
    </>
  );
}

export default CertificationList;
